import { useQuery } from '@tanstack/react-query';
import type { TaxGroup } from '@masamune/shared';
import { Icon } from '../../../components/ui/Icon';
import { EmptyState } from '../../../components/ui/EmptyState';
import { useModalStore } from '../../../stores/modal.store';
import { fiscoGruppoQuery } from './fiscoApi';
import { Caricamento, Fascia, Frase, Intestazione, TabellaTasse, Totale } from './parts';

/**
 * IVA: trimestre per trimestre, raggruppata per anno (Andrea l'ha chiesta per
 * anno). Una sola colonna di importo per riga: quanto resta, o quanto è stato
 * versato. I trimestri finiti dentro il piano a rate hanno solo il chip che
 * porta a Debiti a rate: l'importo vive lì (R3). Il prossimo versamento e
 * l'anticipo di dicembre stanno nella fascia, non in tabella.
 */
export function IvaView({ canWrite, onVai }: { canWrite: boolean; onVai: (g: TaxGroup) => void }) {
  const openModal = useModalStore((s) => s.open);
  const q = useQuery(fiscoGruppoQuery('IVA'));
  const d = q.data;
  if (q.isLoading && !d) return <Caricamento />;
  if (!d?.iva) return <EmptyState icon="percent" title="IVA non disponibile" description="Non riesco a leggere l'IVA in questo momento. Riprova tra poco." />;
  const { anni, notaAnticipi, anticipo } = d.iva;

  // L'anticipo di dicembre: se manca la riga la si crea già con la cifra, se c'è senza cifra la si completa.
  const inserisciAnticipo = () => openModal('newTax', anticipo.tax
    ? { tax: anticipo.tax, prefill: { intent: 'cifra', source: 'CONFERMATO' } }
    : {
      prefill: {
        intent: 'cifra',
        kind: 'IVA_TRIMESTRALE',
        year: anticipo.anno,
        period: `${anticipo.anno}-acconto`,
        description: `Anticipo IVA di dicembre ${anticipo.anno}`,
        dueDate: anticipo.data,
        source: 'CONFERMATO',
      },
    });
  const anticipoSenzaCifra = !anticipo.tax || anticipo.tax.amountDue <= 0;

  return (
    <>
      <Frase testo={d.frase} />
      <Fascia
        voci={d.fascia}
        extra={{
          anticipo: canWrite && anticipoSenzaCifra ? (
            <button className="btn btn-sm fis-st-act" onClick={inserisciAnticipo}>
              <Icon name="pencil" size={10} /> Inserisci la cifra
            </button>
          ) : null,
        }}
      />
      {notaAnticipi && (
        <div className="adm-note mt-3">
          <Icon name="help" size={12} className="mute" />
          <span>{notaAnticipi}</span>
        </div>
      )}
      {anni.length === 0 && <div className="fis-vuoto">Nessun trimestre IVA a sistema.</div>}
      {anni.map((a) => (
        <section key={a.anno} className="fis-gruppo mt-5">
          <Intestazione label={`IVA del ${a.anno}`} destra={a.resta != null ? <Totale valore={a.resta} nota="non versata" /> : null} />
          <TabellaTasse righe={a.righe} canWrite={canWrite} onVai={onVai} colQuando="Quando si paga o è stata pagata" colImporto="Importo da versare o versato" />
        </section>
      ))}
    </>
  );
}
